import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'

import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'
import { isEqual } from 'lodash'

import {
  useColumnVisibility,
  Drawer,
  ToggleButton
} from '~/components/ColumnSelector'
import HeaderPages from '~/components/HeaderPages'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import { formatDateToString, formatSearchString, history } from '~/helpers'
import handleQueryParams from '~/helpers/handleQueryParams'
import { handleSortDirection } from '~/helpers/sortData'
import { useAlert } from '~/hooks/useAlert'
import useFetchSWR from '~/hooks/useFetchSWR'
import useQueryParams from '~/hooks/useQuery'
import ExportButton from '~/modules/retailMedia/components/ExportButton'
import LookbackSelector from '~/modules/retailMedia/components/LookbackSelector'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import { formatIndustryListPublisher } from '~/modules/retailMedia/dtos/industry/publisher/list'
import {
  getNtbOrdersWindow,
  hasVisibleNtbColumn
} from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { useClickMetricsCutoffAlert } from '~/modules/retailMedia/hooks/useClickMetricsCutoffAlert'
import { useNtbAvailability } from '~/modules/retailMedia/hooks/useNtbAvailability'
import { reportPublishersBaseURL } from '~/modules/retailMedia/services/industry/publisher'
import { Footer } from '~/pages/products/list/styles'
import { useAppSelector } from '~/store/hooks'

import PublisherListFilters from '../PublisherListFilters'
import { schema } from './schema'

const dynamicKeys: string[] = [
  'start_date',
  'end_date',
  'publisher_name',
  'quantity',
  'lookback_months'
]

const PUBLISHER_TABLE_READONLY_KEYS = [
  'publisher',
  'balance'
] as const satisfies ReadonlyArray<keyof ReturnType<typeof schema>>

type Params = {
  page: number
  start_date: string
  end_date: string
  publisher_name: string
  account_info: boolean
  quantity: number
  order_direction: string
  order_by: string
  count: boolean
  lookback_months: number
}

const PublisherTable = () => {
  const [queryParams, setQueryParams] = useState<Params>({} as Params)
  const [innerLoading, setInnerLoading] = useState(false)
  const [response, setResponse] = useState<ResponseListIndustryPublishers>()
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)

  const searchParams = useQueryParams()
  const page = Number(searchParams.get('page')) || 1
  const quantity = Number(searchParams.get('quantity')) || 10
  const publisher_name = searchParams.get('publisher_name')
  const order_direction = searchParams.get('order_direction') as SortDirection
  const order_by = searchParams.get('order_by')
  const lookbackMonths = parseLookbackMonths(
    searchParams.get('lookback_months')
  )

  const {
    datepickerHeader: { startDate, endDate }
  } = useAppSelector(state => state)

  const handleOpenColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(true)
  }, [])

  const handleCloseColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(false)
  }, [])

  const params = useMemo(
    () => ({
      page,
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      publisher_name,
      account_info: true,
      quantity,
      order_direction,
      order_by,
      count: true,
      lookback_months: lookbackMonths
    }),
    [
      page,
      startDate,
      endDate,
      publisher_name,
      quantity,
      order_direction,
      order_by,
      lookbackMonths
    ]
  )

  /**
   * Handle request
   */
  const requestURL = useMemo(
    () => `${reportPublishersBaseURL}?${formatSearchString(queryParams)}`,
    [queryParams]
  )

  const {
    data: dataSWR,
    error,
    isValidating
  } = useFetchSWR<ResponseListIndustryPublishers>({
    url: requestURL,
    refreshInterval: 60000,
    revalidateOnFocus: false,
    shouldFetch: !!(queryParams?.start_date && queryParams?.end_date)
  })

  const [AlertOnError] = useAlert(error)

  useEffect(() => {
    if (dataSWR) {
      setResponse(dataSWR)
    }
  }, [dataSWR])

  const { data, total } = useMemo(() => {
    const formattedData = response?.data
      ? formatIndustryListPublisher(response.data)
      : null

    return {
      total: response?.total,
      data: formattedData
    }
  }, [response])

  useEffect(() => {
    setInnerLoading(false)
  }, [response])

  // NTB só para anunciante liberado por conta (allow_ntb_metrics)
  const metricsSources = useMemo(
    () => data?.map(item => item?.metrics?.raw || null) || [],
    [data]
  )
  const availableMetrics = useNtbAvailability(metricsSources)

  const showClickAlert = useClickMetricsCutoffAlert()

  const schemaWithMetrics = useMemo(
    () =>
      schema({
        availableMetrics,
        showClickAlert,
        lookbackMonths,
        ntbOrdersWindow: getNtbOrdersWindow(endDate)
      }),
    [availableMetrics, showClickAlert, lookbackMonths, endDate]
  )

  const { columns, visibleColumnsSchema, onColumnsChange, onResetToDefault } =
    useColumnVisibility({
      schema: schemaWithMetrics,
      tableName: 'publishers',
      readonlyKeys: PUBLISHER_TABLE_READONLY_KEYS
    })

  /**
   * Handle Pagination
   */
  const handlePagination = useCallback(
    (value: number) => {
      searchParams.set('page', String(value))
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const handleItemsPerPageChange = useCallback(
    (value: { value: number }) => {
      searchParams.set('quantity', String(value.value))
      searchParams.set('page', '1')
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const handleLookbackChange = useCallback(
    (value: number) => {
      searchParams.set('lookback_months', String(value))
      searchParams.set('page', '1')
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  // seletor de lookback só aparece quando há coluna NTB visível/selecionada
  const hasNtbMetrics = !!hasVisibleNtbColumn(visibleColumnsSchema)
  /**
   * Sort list
   */

  const handleSortList = useCallback(
    (data: OnSortProps) => {
      const selectedKey = data.key
      const sortInitialDirection = data?.sortInitialDirection

      const response = handleSortDirection({
        selectedKey,
        currentSortDirection: order_direction,
        currentSortKey: order_by,
        sortInitialDirection,
        allowReset: true
      })

      if (!response.sortDirection) {
        searchParams.delete('order_direction')
        searchParams.delete('order_by')
      }

      if (response.sortDirection) {
        searchParams.set('order_direction', response.sortDirection)
        searchParams.set('order_by', response.sortKey)
      }

      history.push({ search: searchParams.toString() })
    },
    [order_by, order_direction, searchParams]
  )

  /**
   * Handle coleteral effect
   */
  useLayoutEffect(() => {
    if (isEqual(params, queryParams)) {
      return
    }

    const { shouldResetFixedKeys } = handleQueryParams({
      params,
      currentParams: queryParams,
      dynamicKeys
    })

    if (shouldResetFixedKeys) {
      params.page = 1
      handlePagination(1)
    }

    setInnerLoading(true)
    setQueryParams(params)
  }, [handlePagination, params, queryParams])

  return (
    <>
      <HeaderPages
        title={t('rm:PublishersList')}
        sideRightComponent={
          <>
            {hasNtbMetrics && (
              <LookbackSelector
                value={lookbackMonths}
                onChange={handleLookbackChange}
              />
            )}
            <ToggleButton onClick={handleOpenColumnSelector} />
            <ExportButton
              queryParams={formatSearchString(queryParams)}
              exportType={'publishers'}
            />
          </>
        }
      />
      <Drawer
        columns={columns}
        isOpen={isColumnSelectorOpen}
        onClose={handleCloseColumnSelector}
        onColumnsChange={onColumnsChange}
        onResetToDefault={onResetToDefault}
      />

      <section>
        <PublisherListFilters />
      </section>

      <Loading status={!data && isValidating}>Carregando publishers...</Loading>

      <LoadingPopUp isActive={isValidating} />

      <AlertOnError />

      <Panel noPaddingBody isLoading={!data}>
        <Loading status={innerLoading} onlyLine />

        {!!data && (
          <Table
            data={data}
            schema={visibleColumnsSchema}
            total={total}
            smallerRowPadding
            sideBorder
            freeze="first-row-and-column"
            // sort settings
            activeSortDirection={order_direction}
            activeSortKey={order_by}
            onSort={handleSortList}
          />
        )}

        {total === 0 && data && (
          <Alert variant="warning">{t('rm:table.publisher.notFound')}</Alert>
        )}
      </Panel>

      <Footer>
        <ListPagination
          total={total}
          label={total > 1 ? 'publishers' : 'publisher'}
          currentPage={page}
          itemsPerPage={quantity}
          menuPlacement="top"
          onClickPagination={handlePagination}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Footer>
    </>
  )
}

export default PublisherTable
